import React, { useState } from 'react';
import config from '../config';

/**
 * ForgotPasswordForm component for self-service password reset.
 *
 * Flow:
 * 1. User enters their username (email)
 * 2. Cognito sends a verification code to their email
 * 3. User enters the code + a new password
 * 4. Password is reset and user can log in
 */
const ForgotPasswordForm = ({ onCancel, onPasswordReset }) => {
  const [step, setStep] = useState('request'); // 'request' or 'confirm'
  const [username, setUsername] = useState('');
  const [code, setCode] = useState('');
  const [newPassword, setNewPassword] = useState('');
  const [confirmPassword, setConfirmPassword] = useState('');
  const [error, setError] = useState('');
  const [success, setSuccess] = useState('');
  const [isLoading, setIsLoading] = useState(false);

  const baseUrl = config.api.authServiceUrl;

  /**
   * Request a password reset code be sent to the user's email.
   */
  const handleRequestCode = async (e) => {
    e.preventDefault();
    setError('');
    setSuccess('');
    setIsLoading(true);

    try {
      const response = await fetch(`${baseUrl}/auth/forgot-password`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ username }),
      });

      const data = await response.json();

      if (!response.ok) {
        throw new Error(data.detail || 'Failed to send reset code');
      }

      setSuccess('If your account exists, a verification code has been sent to your email.');
      setStep('confirm');
    } catch (err) {
      setError(err.message || 'Failed to send reset code');
    } finally {
      setIsLoading(false);
    }
  };

  /**
   * Confirm the password reset with the verification code and new password.
   */
  const handleConfirmReset = async (e) => {
    e.preventDefault();
    setError('');
    setSuccess('');

    if (newPassword !== confirmPassword) {
      setError('Passwords do not match');
      return;
    }

    if (newPassword.length < 8) {
      setError('Password must be at least 8 characters');
      return;
    }

    setIsLoading(true);

    try {
      const response = await fetch(`${baseUrl}/auth/confirm-forgot-password`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          username,
          confirmation_code: code,
          new_password: newPassword,
        }),
      });

      const data = await response.json();

      if (!response.ok) {
        throw new Error(data.detail || 'Failed to reset password');
      }

      setSuccess('Password reset successfully! You can now log in with your new password.');

      // Wait briefly so user can see the success message, then go back to login
      setTimeout(() => {
        if (onPasswordReset) {
          onPasswordReset();
        } else {
          onCancel();
        }
      }, 2000);
    } catch (err) {
      setError(err.message || 'Failed to reset password');
    } finally {
      setIsLoading(false);
    }
  };

  // Step 1: Request reset code
  if (step === 'request') {
    return (
      <div className="login-container">
        <div className="login-form">
          <h2>Reset Password</h2>
          <p className="text-muted mb-4">
            Enter your username and we'll send a verification code to your email.
          </p>

          {error && <div className="alert alert-danger">{error}</div>}
          {success && <div className="alert alert-success">{success}</div>}

          <form onSubmit={handleRequestCode}>
            <div className="mb-3">
              <label htmlFor="username" className="form-label">Username</label>
              <input
                type="text"
                className="form-control"
                id="username"
                value={username}
                onChange={(e) => setUsername(e.target.value)}
                placeholder="Enter your username"
                required
                autoFocus
              />
            </div>

            <button
              type="submit"
              className="btn btn-primary w-100 mb-2"
              disabled={isLoading}
            >
              {isLoading ? 'Sending...' : 'Send Reset Code'}
            </button>

            <button
              type="button"
              className="btn btn-outline-secondary w-100"
              onClick={onCancel}
            >
              Back to Login
            </button>
          </form>
        </div>
      </div>
    );
  }

  // Step 2: Enter code and new password
  return (
    <div className="login-container">
      <div className="login-form">
        <h2>Reset Password</h2>
        <p className="text-muted mb-4">
          Enter the verification code sent to your email and choose a new password.
        </p>
        <div className="alert alert-info py-2">
          <small>Check Barracuda, this email may have been caught as spam.</small>
        </div>

        {error && <div className="alert alert-danger">{error}</div>}
        {success && <div className="alert alert-success">{success}</div>}

        <form onSubmit={handleConfirmReset}>
          <div className="mb-3">
            <label htmlFor="username" className="form-label">Username</label>
            <input
              type="text"
              className="form-control"
              id="username"
              value={username}
              disabled
            />
          </div>

          <div className="mb-3">
            <label htmlFor="code" className="form-label">Verification Code</label>
            <input
              type="text"
              className="form-control"
              id="code"
              value={code}
              onChange={(e) => setCode(e.target.value)}
              placeholder="Enter the code from your email"
              required
              autoFocus
            />
          </div>

          <div className="mb-3">
            <label htmlFor="newPassword" className="form-label">New Password</label>
            <input
              type="password"
              className="form-control"
              id="newPassword"
              value={newPassword}
              onChange={(e) => setNewPassword(e.target.value)}
              placeholder="Enter new password"
              required
            />
          </div>

          <div className="mb-3">
            <label htmlFor="confirmPassword" className="form-label">Confirm Password</label>
            <input
              type="password"
              className="form-control"
              id="confirmPassword"
              value={confirmPassword}
              onChange={(e) => setConfirmPassword(e.target.value)}
              placeholder="Confirm new password"
              required
            />
          </div>

          <button
            type="submit"
            className="btn btn-primary w-100 mb-2"
            disabled={isLoading}
          >
            {isLoading ? 'Resetting...' : 'Reset Password'}
          </button>

          <button
            type="button"
            className="btn btn-outline-secondary w-100"
            onClick={onCancel}
          >
            Back to Login
          </button>
        </form>

        <div className="mt-3 text-center">
          <small>
            <button
              type="button"
              className="btn btn-link btn-sm p-0"
              onClick={() => { setStep('request'); setError(''); setSuccess(''); }}
            >
              Didn't receive a code? Send again
            </button>
          </small>
        </div>
      </div>
    </div>
  );
};

export default ForgotPasswordForm;
